import type { Employee } from "./types";

/**
 * Who belongs on a pay-period entry roster.
 *
 * Deactivate is a soft flag — tip rows stay in the database. Entry screens
 * must still show anyone with records in the period (and anyone who left
 * during it) so history remains visible and a later save cannot drop them
 * from replace-style writes (e.g. kitchen tipouts).
 */
export function employeesForPeriod(
  employees: Employee[],
  period: { start_date: string; end_date: string } | null,
  idsWithRecords: Iterable<string> = [],
): Employee[] {
  if (!period) return employees.filter((e) => e.is_active);
  const recorded = idsWithRecords instanceof Set ? idsWithRecords : new Set(idsWithRecords);
  return employees.filter((e) => isEmployeeOnPeriodRoster(e, period, recorded));
}

export function isEmployeeOnPeriodRoster(
  emp: Employee,
  period: { start_date: string; end_date: string },
  idsWithRecords: Set<string>,
): boolean {
  if (idsWithRecords.has(emp.id)) return true;
  if (emp.start_date && emp.start_date > period.end_date) return false;
  if (emp.is_active) {
    if (emp.end_date && emp.end_date < period.start_date) return false;
    return true;
  }
  // Left during this period — still need them on the roster for remaining days.
  return Boolean(
    emp.end_date &&
      emp.end_date >= period.start_date &&
      emp.end_date <= period.end_date,
  );
}

export function employeeDisplayName(emp: Pick<Employee, "name" | "is_active">): string {
  return emp.is_active ? emp.name : `${emp.name} (inactive)`;
}

export function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => (seen.has(item.id) ? false : (seen.add(item.id), true)));
}
